import React, { useState, useEffect } from 'react';
import { teamsAPI } from '../api/client';
import {
  Bot, Users, ToggleLeft, ToggleRight, Bell, BellOff,
  Calendar, CheckCircle, XCircle, Activity, Wifi, WifiOff,
  RefreshCw, TrendingUp, MessageSquare, Check
} from 'lucide-react';

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-700',
    green:  'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
    amber:  'bg-amber-50 text-amber-700',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

export default function CompanyTeamsSettings() {
  const [users, setUsers]       = useState([]);
  const [usage, setUsage]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});
  const [msg, setMsg]           = useState('');
  const [tab, setTab]           = useState('users'); // users | usage

  useEffect(() => {
    Promise.all([teamsAPI.getUsers(), teamsAPI.getUsage()])
      .then(([u, us]) => {
        setUsers(u.data);
        setUsage(us.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (userId, field, currentVal) => {
    setSaving(s => ({ ...s, [userId + field]: true }));
    try {
      await teamsAPI.updateUser(userId, { [field]: !currentVal });
      setUsers(prev => prev.map(u =>
        u.UserID === userId
          ? { ...u, [field === 'enabled' ? 'TeamsEnabled' : field === 'morningAlert' ? 'MorningAlert' : 'StandupEnabled']: !currentVal }
          : u
      ));
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch {
      setMsg('Failed to save');
    } finally {
      setSaving(s => ({ ...s, [userId + field]: false }));
    }
  };

  const summary = usage?.summary;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <Bot className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Teams Integration</h1>
          <p className="text-sm text-gray-500">Manage Microsoft Teams bot access for your team</p>
        </div>
        {msg && (
          <span className="ml-auto flex items-center gap-1 text-sm text-green-600 font-medium">
            <Check className="w-4 h-4" /> {msg}
          </span>
        )}
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Users}        label="Teams Enabled"     value={summary.EnabledUsers}    color="blue" />
          <StatCard icon={Wifi}         label="Bot Connected"     value={summary.ConnectedUsers}  color="green" />
          <StatCard icon={Activity}     label="Events (7 days)"   value={summary.EventsLast7d}    color="purple" />
          <StatCard icon={CheckCircle}  label="Approvals (30d)"   value={summary.ApprovalsLast30d} color="amber" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[['users','Users'], ['usage','Usage Log']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : tab === 'users' ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-center px-4 py-3">Teams Access</th>
                <th className="text-center px-4 py-3">Bot Connected</th>
                <th className="text-center px-4 py-3">Morning Alert</th>
                <th className="text-center px-4 py-3">AI Standup</th>
                <th className="text-left px-4 py-3">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => {
                const connected = !!u.ConversationRef;
                return (
                  <tr key={u.UserID} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{u.FirstName} {u.LastName}</p>
                      <p className="text-xs text-gray-400">{u.Email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(u.UserID, 'enabled', u.TeamsEnabled)}
                        disabled={saving[u.UserID + 'enabled']}
                        className={`flex items-center gap-1 mx-auto px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          u.TeamsEnabled
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {u.TeamsEnabled ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                        {u.TeamsEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {connected
                        ? <span className="inline-flex items-center gap-1 text-green-600 text-xs"><Wifi className="w-3.5 h-3.5" /> Connected</span>
                        : <span className="inline-flex items-center gap-1 text-gray-400 text-xs"><WifiOff className="w-3.5 h-3.5" /> Not connected</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(u.UserID, 'morningAlert', u.MorningAlert)}
                        disabled={!u.TeamsEnabled || saving[u.UserID + 'morningAlert']}
                        className={`mx-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors disabled:opacity-40 ${
                          u.MorningAlert ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {u.MorningAlert ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                        {u.MorningAlert ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(u.UserID, 'standupEnabled', u.StandupEnabled)}
                        disabled={!u.TeamsEnabled || saving[u.UserID + 'standupEnabled']}
                        className={`mx-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors disabled:opacity-40 ${
                          u.StandupEnabled ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {u.StandupEnabled ? <Calendar className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                        {u.StandupEnabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {fmt(u.EnabledAt)}
                    </td>
                  </tr>
                );
              })}
              {!users.length && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Usage tab */
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Recent Activity</span>
            </div>
            <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
              {(usage?.recent || []).map((row, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="text-gray-700 font-medium">{row.UserName || 'Unknown'}</span>
                    <span className="text-gray-400 text-xs">{row.ActivityType?.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-xs text-gray-400">{fmt(row.CreatedAt)}</span>
                </div>
              ))}
              {!(usage?.recent?.length) && (
                <p className="px-4 py-6 text-center text-sm text-gray-400">No activity yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Setup guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">How to connect</p>
        <ol className="list-decimal list-inside space-y-1 text-blue-700">
          <li>Install the SpocoDoc app from the Teams app store (or sideload the provided ZIP)</li>
          <li>Open a chat with the SpocoDoc bot</li>
          <li>The bot will automatically link your Teams account to SpocoDoc via your Azure AD email</li>
        </ol>
      </div>
    </div>
  );
}
